import { Node, wrappingInputRule, getNodeType, findParentNode, mergeAttributes, findChildren } from '@tiptap/core';
import { DropdownItemTitleName } from './DropdownItemTitle.js';
import { DropdownItemContentName } from './DropdownItemContent.js';
import { Plugin, PluginKey, NodeSelection, TextSelection } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { DropdownItemComponent } from './DropdownItemComponent.js';
import { isNumber } from '@fabric-app/utils/guards';

const inputRegex = /^\s*>\s$/;
const isReplaceAroundStep = (step) => {
  return "from" in step && isNumber(step.from) && "to" in step && isNumber(step.to) && "gapFrom" in step && isNumber(step.gapFrom) && "gapTo" in step && isNumber(step.gapTo) && "insert" in step && isNumber(step.insert);
};
const DropdownItemName = "dropdownItem";
const DropdownItem = Node.create({
  name: DropdownItemName,
  addNodeView() {
    return ReactNodeViewRenderer(DropdownItemComponent, {
      attrs: {
        ["data-renderer-node-type"]: DropdownItemName
      }
    });
  },
  addOptions() {
    return {
      HTMLAttributes: {},
      titleTypeName: DropdownItemTitleName,
      contentTypeName: DropdownItemContentName
    };
  },
  addAttributes() {
    return {
      expanded: {
        default: true,
        parseHTML: (element) => element.getAttribute("data-expanded"),
        renderHTML: (attributes) => ({
          "data-expanded": attributes["data-expanded"]
        }),
        keepOnSplit: true
      }
    };
  },
  group: "block",
  content() {
    return `${this.options.titleTypeName} ${this.options.contentTypeName}?`;
  },
  addCommands() {
    return {
      liftDropdownItem: () => ({ tr, state }) => {
        const isActive = this.editor.isActive(this.name);
        const found = findParentNode((node2) => node2.type === this.type)(state.selection);
        if (!isActive || !found) {
          return false;
        }
        const selectionFrom = state.selection.from;
        const { pos, node } = found;
        const from = pos;
        const to = pos + node.nodeSize;
        const titleType = getNodeType(this.options.titleTypeName, state.schema);
        const contentType = getNodeType(this.options.contentTypeName, state.schema);
        const title = findChildren(node, (child) => child.type === titleType)[0];
        const content = findChildren(node, (child) => child.type === contentType)[0];
        if (!title || !content) {
          return false;
        }
        const fragment = title.node.content.append(content.node.content);
        tr.replaceWith(from, to, fragment).setSelection(TextSelection.create(tr.doc, selectionFrom - 2));
        return true;
      },
      setDropdownItem: () => ({ chain, editor, state }) => {
        const isActive = editor.isActive(this.name);
        if (isActive) {
          return false;
        }
        const dropdownItemTitle = getNodeType(this.options.titleTypeName, state.schema);
        const dropdownItemContent = getNodeType(this.options.contentTypeName, state.schema);
        const titleContentMatch = dropdownItemTitle.contentMatch;
        const contentContentMatch = dropdownItemContent.contentMatch;
        const { from, to, $from } = state.selection;
        if (state.selection.empty) {
          const cursorNode = $from.node();
          const listItemType = getNodeType("listItem", state.schema);
          const listItem = findParentNode((node) => node.type === listItemType)(state.selection);
          if (listItem) {
            const title = dropdownItemTitle.create({}, listItem.node.child(0).copy(listItem.node.child(0).content));
            const content = dropdownItemContent.create({}, listItem.node.slice(1).content);
            const dropdownNode2 = this.type.create({}, [
              title,
              ...content.childCount > 0 ? [
                content
              ] : []
            ]);
            console.log(state.selection);
            let listItemStart = listItem.start;
            let listItemEnd = listItem.start + listItem.node.nodeSize;
            return chain().command(({ commands }) => {
              commands.liftListItem("listItem");
              return true;
            }).command(({ chain: chain2, state: state2 }) => {
              const diff = state2.selection.$from.start() - listItemStart;
              listItemStart += diff;
              listItemEnd -= listItem.depth - diff;
              return chain2().deleteRange({
                from: listItemStart,
                to: listItemEnd
              }).run();
            }).command(({ chain: chain2 }) => {
              return chain2().insertContentAt(listItemStart, dropdownNode2).setTextSelection(listItemStart + title.nodeSize).run();
            }).run();
          }
          if (titleContentMatch.matchType(cursorNode.type)) {
            const title = dropdownItemTitle.create({}, cursorNode.copy(cursorNode.content));
            const dropdownNode2 = this.type.create({}, [
              title
            ]);
            return chain().deleteRange({
              from: $from.start(),
              to: $from.end()
            }).insertContentAt($from.start(), dropdownNode2).setTextSelection($from.start() + title.nodeSize).run();
          }
        }
        const slice = state.doc.slice(from, to);
        const nodes = slice.content;
        if (nodes.size === 0) {
          return false;
        }
        const firstNode = nodes.firstChild;
        if (!firstNode) {
          return false;
        }
        const canBeTitle = titleContentMatch.matchType(firstNode.type);
        const titleNode = canBeTitle ? dropdownItemTitle.create({}, firstNode.type.create(firstNode.attrs, firstNode.content)) : dropdownItemTitle.create({}, state.schema.nodes.paragraph.create());
        const contentNodes = [];
        const startIdx = canBeTitle ? 1 : 0;
        for (let i = startIdx; i < nodes.size; i++) {
          const node = nodes.child(i);
          if (contentContentMatch.matchType(node.type)) {
            contentNodes.push(node);
          }
        }
        const contentNode = dropdownItemContent.create({}, contentNodes);
        const dropdownNode = this.type.create({}, [
          titleNode,
          ...contentNode.childCount > 0 ? [
            contentNode
          ] : []
        ]);
        return chain().deleteRange({
          from,
          to
        }).insertContentAt(from, dropdownNode).run();
      },
      toggleDropdownItem: () => ({ commands }) => {
        const isActive = this.editor.isActive(this.name);
        if (isActive) {
          return commands.liftDropdownItem();
        }
        return commands.setDropdownItem();
      },
      splitDropdownItem: () => ({ chain, editor, state, commands }) => {
        const titleType = getNodeType(this.options.titleTypeName, state.schema);
        const contentType = getNodeType(this.options.contentTypeName, state.schema);
        const paragraphType = getNodeType("paragraph", state.schema);
        const isActiveTitle = editor.isActive(titleType.name);
        if (!isActiveTitle) {
          return false;
        }
        const { from, to } = state.selection;
        if (from !== to) {
          return false;
        }
        const parent = findParentNode((node) => node.type === this.type)(state.selection);
        const title = findParentNode((node) => node.type === titleType)(state.selection);
        if (!parent || !title) {
          return false;
        }
        const { pos: titlePos, node: titleNode } = title;
        const { pos, node: parentNode } = parent;
        if (titleNode.textContent.length === 0) {
          return commands.liftDropdownItem();
        }
        if (from === titlePos) {
          const title2 = titleType.create({}, [
            paragraphType.create({}, [])
          ]);
          const dropdownNode = this.type.create({
            expanded: false
          }, [
            title2
          ]);
          return chain().insertContentAt(pos, dropdownNode, {
            updateSelection: false
          }).run();
        }
        const positionInTitle = from - titlePos - 2;
        const firstChildTitle = titleNode.firstChild;
        if (!firstChildTitle) {
          return false;
        }
        const splitOffContent = firstChildTitle.copy(firstChildTitle.content.cut(positionInTitle));
        const cutAmount = titlePos + titleNode.nodeSize - from;
        if (!parent.node.attrs.expanded) {
          const title2 = titleType.create({}, [
            splitOffContent
          ]);
          const dropdownNode = this.type.create({
            expanded: false
          }, [
            title2
          ]);
          const newPos = pos + parentNode.nodeSize - cutAmount + 1;
          return chain().deleteRange({
            from,
            to: titlePos + titleNode.nodeSize
          }).insertContentAt(newPos, dropdownNode).selectTextblockStart().run();
        }
        const contentChild = findChildren(parentNode, (child) => child.type === contentType)[0];
        if (!contentChild) {
          const content = contentType.create({}, splitOffContent.content.size > 0 ? [
            splitOffContent
          ] : [
            paragraphType.create({}, [])
          ]);
          return chain().deleteRange({
            from,
            to: titlePos + titleNode.nodeSize
          }).insertContentAt(pos + parentNode.nodeSize - cutAmount - 1, content).command(({ chain: chain2 }) => {
            if (splitOffContent.textContent.length === 0) {
              return true;
            }
            return chain2().setTextSelection(pos + parentNode.nodeSize - cutAmount + 2).run();
          }).run();
        }
        return chain().insertContentAt(titlePos + titleNode.nodeSize + 1, splitOffContent).selectTextblockStart().deleteRange({
          from,
          to: titlePos + titleNode.nodeSize
        }).run();
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: `div[data-node-type="${DropdownItemName}"]`
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        ["data-node-type"]: DropdownItemName
      }),
      0
    ];
  },
  addProseMirrorPlugins() {
    const contentTypeName = this.options.contentTypeName;
    const dropdownType = this.type;
    return [
      new Plugin({
        key: new PluginKey("dropdownItem"),
        appendTransaction(transactions, _, newState) {
          const contentNodeType = getNodeType(contentTypeName, newState.schema);
          const newTr = newState.tr;
          for (const tr of transactions) {
            for (const step of tr.steps) {
              if (isReplaceAroundStep(step)) {
                const { from, gapFrom, gapTo, slice } = step;
                if (slice.content.maybeChild(0)?.type !== dropdownType) {
                  continue;
                }
                if (gapFrom === gapTo || from >= gapFrom) {
                  continue;
                }
                const selection = NodeSelection.create(tr.doc, from);
                const parentContent = findParentNode((node) => node.type === contentNodeType)(selection);
                const parentDropdown = findParentNode((node) => node.type === dropdownType)(selection);
                if (!parentContent || !parentDropdown || parentDropdown.node.attrs.expanded) {
                  continue;
                }
                newTr.setNodeAttribute(parentDropdown.pos, "expanded", true);
              }
            }
          }
          if (newTr.docChanged) {
            return newTr;
          }
          return null;
        },
        props: {
          handleDrop: (view, event, slice, moved) => {
            const target = event.target;
            if (!(target instanceof globalThis.HTMLElement)) {
              return false;
            }
            const targetNode = target.closest("[data-dropdown-item-empty-container]");
            if (!targetNode) {
              return false;
            }
            const targetPos = view.posAtDOM(targetNode, event.offsetX);
            if (targetPos === null) {
              return false;
            }
            const editor = this.editor;
            const paragraph = editor.schema.nodes.paragraph.create({}, []);
            const content = editor.schema.nodes.dropdownItemContent.create({}, slice.size > 0 ? slice.content : paragraph);
            return editor.chain().command(({ chain }) => {
              if (!moved) return true;
              return chain().deleteSelection().run();
            }).insertContentAt(targetPos, content).setTextSelection(targetPos + 1).run();
          }
        }
      })
    ];
  },
  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type
      })
    ];
  }
});

export { DropdownItem, DropdownItemName, inputRegex };
//# sourceMappingURL=DropdownItem.js.map
